"use client";

import React, { useEffect } from "react";
import { MapPin, Phone, Mail, ArrowRight, CircleCheck, Loader } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const SHARED_DATA = {
  address: "123 Serenity Lane, McKinney, TX 75070",
  phone: "(469) 555-0123",
  email: "care@moonriseresidential.com",
  business_name: "Moonrise Residential Care"
};
function TransformationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Inquiry received! We'll be in touch shortly.", {
        description: "We look forward to speaking with your family.",
        icon: <CircleCheck className="text-green-500" />
      });
    }
  }, [isSubmitted, submitError]);
  if (isSubmitted && !submitError) {
    return <div data-section-id="22734" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-green-50 flex items-center justify-center">
          <CircleCheck className="size-10 text-green-600" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-[#091b3a] font-sans">Message Sent!</h3>
          <p className="text-muted-foreground max-w-xs mx-auto font-sans">
            Thank you for reaching out to Moonrise Residential Care. Our care coordinator will contact you within one business day.
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-full border-[#ffbd00] text-[#ffbd00] hover:bg-[#ffbd00] hover:text-white font-sans btn-primary">
          Send Another Inquiry
        </Button>
      </div>;
  }
  const inputClasses = "peer flex h-14 w-full rounded-lg border border-input bg-transparent px-4 pt-5 pb-2 text-sm shadow-sm transition-all placeholder:text-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffbd00] focus-visible:border-[#ffbd00] disabled:cursor-not-allowed disabled:opacity-50 font-sans";
  const labelClasses = "absolute left-4 top-4 z-10 origin-[0] -translate-y-2.5 scale-75 transform text-muted-foreground duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-2.5 peer-focus:scale-75 peer-focus:text-[#ffbd00] cursor-text font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-5 text-black">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <input {...field} placeholder="Full Name" className={inputClasses} />
              </FormControl>
              <FormLabel className={labelClasses}>Your Full Name</FormLabel>
              <FormMessage className="absolute -bottom-5 left-0 text-xs text-destructive" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <input {...field} type="email" placeholder="Email Address" className={inputClasses} />
              </FormControl>
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormMessage className="absolute -bottom-5 left-0 text-xs text-destructive" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <input {...field} type="tel" placeholder="Phone Number" className={inputClasses} />
              </FormControl>
              <FormLabel className={labelClasses}>Phone Number (Optional)</FormLabel>
              <FormMessage className="absolute -bottom-5 left-0 text-xs text-destructive" />
            </FormItem>} />

        {}
        <FormField control={control} name="company_name" rules={{
        required: "Family or Resident name is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <input {...field} placeholder="Family or Resident Name" className={inputClasses} />
              </FormControl>
              <FormLabel className={labelClasses}>Family or Resident Name</FormLabel>
              <FormMessage className="absolute -bottom-5 left-0 text-xs text-destructive" />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="desired_start_date" render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <input {...field} type="date" placeholder="Preferred Move-in Date" className={inputClasses} />
            </FormControl>
            <FormLabel className="absolute left-4 top-1 text-xs text-[#ffbd00] scale-75 origin-[0] transform -translate-y-0 font-sans">
              Preferred Move-in Date
            </FormLabel>
            <FormMessage className="absolute -bottom-5 left-0 text-xs text-destructive" />
          </FormItem>} />

      {}
      <FormField control={control} name="project_overview" rules={{
      required: "Please tell us how we can help"
    }} render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <textarea {...field} placeholder="Care Needs & Questions" className={`${inputClasses} h-32 resize-none py-6`} />
            </FormControl>
            <FormLabel className={labelClasses}>Care Needs & Questions</FormLabel>
            <FormMessage className="absolute -bottom-5 left-0 text-xs text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-[#ffbd00] text-white hover:bg-[#ffbd00]/90 shadow-lg shadow-[#ffbd00]/20 rounded-full transition-all duration-300 font-sans btn-primary">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Sending Inquiry...
            </> : <>
              Send Inquiry <ArrowRight className="ml-2 size-5" />
            </>}
        </Button>
        <p className="text-xs text-center text-muted-foreground mt-4 font-sans">
          By submitting, you agree to our terms of service regarding family privacy and care inquiries.
        </p>
      </div>
    </Form>;
}
export default function StartYourTransformation() {
  return <section id="get-in-touch" className="relative w-full bg-white overflow-hidden font-sans">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-[#ffbd00]/5 rounded-full blur-3xl" />
        <div className="absolute top-[40%] right-[0%] w-[40%] h-[60%] bg-[#091b3a]/5 rounded-full blur-3xl" />
      </div>

      <div className="flex flex-col lg:flex-row min-h-screen">
        {}
        <div className="w-full lg:w-5/12 bg-[#091b3a] text-white relative flex flex-col justify-between p-8 lg:p-12 xl:p-16 overflow-hidden">
          {}
          <div className="absolute top-0 right-0 w-px h-full bg-white/10 hidden lg:block" />
          <div className="absolute -right-20 top-0 w-40 h-full bg-white/5 skew-x-12 hidden lg:block" />

          <div className="relative z-10 space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 font-sans">
                Connect With <br />
                <span className="text-[#ffbd00]">Our Care Team</span>
              </h2>
              <p className="text-white/80 text-lg leading-relaxed max-w-md font-sans">
                Ready to find the perfect home for your loved one? Our compassionate team is here to guide your family through every step of the journey.
              </p>
            </div>

            <div className="space-y-6 py-8">
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg bg-white/5 border border-white/10 group-hover:border-[#ffbd00]/50 group-hover:bg-[#ffbd00]/10 transition-colors">
                  <MapPin className="size-6 text-[#ffbd00]" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg font-sans">Visit Our Home</h4>
                  <p className="text-white/70 font-sans">2703 Cheverny Dr, McKinney, TX 75070</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg bg-white/5 border border-white/10 group-hover:border-[#ffbd00]/50 group-hover:bg-[#ffbd00]/10 transition-colors">
                  <Phone className="size-6 text-[#ffbd00]" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg font-sans">Call Us Directly</h4>
                  <p className="text-white/70 font-sans">972.987.0475 / 469.922.3702</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg bg-white/5 border border-white/10 group-hover:border-[#ffbd00]/50 group-hover:bg-[#ffbd00]/10 transition-colors">
                  <Mail className="size-6 text-[#ffbd00]" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg font-sans">Email Inquiries</h4>
                  <p className="text-white/70 font-sans">hello@moonriseresidentialcare.com</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="relative z-10 mt-auto pt-8">
            <div className="w-full h-48 rounded-2xl overflow-hidden border border-white/10 shadow-2xl relative group">
              <div className="absolute inset-0 bg-[#091b3a]/20 z-10 pointer-events-none group-hover:bg-transparent transition-colors duration-500" />
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3339.467812345678!2d-96.6436!3d33.1972!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDExJzUwLjAiTiA5NsKwMzgnMzcuMCJX!5e0!3m2!1sen!2sus!4v1634567890123!5m2!1sen!2sus" width="100%" height="100%" style={{
              border: 0,
              filter: "grayscale(100%) opacity(80%)"
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Moonrise Residential Care Location" className="opacity-80 hover:opacity-100 transition-opacity duration-500" />
            </div>
          </div>
        </div>

        {}
        <div className="w-full lg:w-7/12 bg-white p-6 md:p-12 lg:p-16 flex flex-col justify-center relative">
          <div className="max-w-2xl mx-auto w-full">
            <div className="mb-10">
              <h3 className="text-3xl font-bold text-[#091b3a] mb-2 font-sans tracking-tight">Let's Collaborate</h3>
              <p className="text-muted-foreground font-sans text-lg">
                Fill out the form below to share your needs. We typically respond within one business day to schedule a private tour.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-1 md:p-8 border border-border/50 shadow-lg shadow-[#091b3a]/5">
              <FormProvider formKey="contact_form_contact_us" formId="contact_form_contact_us" sectionName="start-transformation" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              company_name: "",
              project_overview: "",
              desired_start_date: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              company_name: {
                required: true
              },
              project_overview: {
                required: true
              }
            }} submitText="Send Inquiry" successMessage="Inquiry received successfully!">
                <TransformationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}